import { useT, useLabels } from "../i18n";
import { useState } from "react";
import { Download, Share2 } from "lucide-react";
import { imgSrc } from "../lib/api";

const W = 1080, H = 1350, ORANGE = "#FF5A1F", BLACK = "#0A0A0A", OFF = "#F5F5F2";

const loadImage = (src) =>
  new Promise((resolve) => {
    if (!src) return resolve(null);
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = imgSrc(src);
  });

const circle = (ctx, img, name, cx, cy, r) => {
  ctx.save();
  ctx.beginPath();
  ctx.arc(cx, cy, r, 0, Math.PI * 2);
  ctx.closePath();
  ctx.clip();
  ctx.fillStyle = "#2A2A2A";
  ctx.fillRect(cx - r, cy - r, r * 2, r * 2);
  if (img) {
    const s = Math.max((r * 2) / img.width, (r * 2) / img.height);
    ctx.drawImage(img, cx - (img.width * s) / 2, cy - (img.height * s) / 2, img.width * s, img.height * s);
  } else {
    ctx.fillStyle = OFF;
    ctx.font = `${r * 0.8}px Anton`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(name.slice(0, 2).toUpperCase(), cx, cy + r * 0.05);
  }
  ctx.restore();
  ctx.lineWidth = 8;
  ctx.strokeStyle = OFF;
  ctx.beginPath();
  ctx.arc(cx, cy, r + 10, 0, Math.PI * 2);
  ctx.stroke();
};

export async function renderShareCard(match, t, categoryLabel) {
  await document.fonts.load("120px Anton").catch(() => {});
  await document.fonts.load("600 40px Inter").catch(() => {});
  const [meImg, partnerImg] = await Promise.all([loadImage(match.me.photo_url), loadImage(match.partner.photo_url)]);
  const canvas = document.createElement("canvas");
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext("2d");
  ctx.fillStyle = BLACK;
  ctx.fillRect(0, 0, W, H);
  for (let i = 0; i < 14000; i++) {
    ctx.fillStyle = `rgba(255,255,255,${Math.random() * 0.05})`;
    ctx.fillRect(Math.random() * W, Math.random() * H, 2, 2);
  }
  ctx.fillStyle = ORANGE;
  ctx.fillRect(0, 0, W, 14);

  ctx.textAlign = "center";
  ctx.textBaseline = "alphabetic";
  ctx.font = "64px Anton";
  ctx.fillStyle = OFF;
  const logo = "RO", x = "X", pair = "PAIR";
  const lw = ctx.measureText(logo).width, xw = ctx.measureText(x).width, pw = ctx.measureText(pair).width;
  let sx = W / 2 - (lw + xw + pw) / 2;
  ctx.textAlign = "left";
  ctx.fillText(logo, sx, 130); sx += lw;
  ctx.fillStyle = ORANGE; ctx.fillText(x, sx, 130); sx += xw;
  ctx.fillStyle = OFF; ctx.fillText(pair, sx, 130);

  ctx.textAlign = "center";
  ctx.font = "150px Anton";
  const t1 = t("img_match_a"), t2 = t("img_match_b"), t3 = t("img_match_c");
  const w1 = ctx.measureText(t1).width, w2 = ctx.measureText(t2).width, w3 = ctx.measureText(t3).width;
  let tx = W / 2 - (w1 + w2 + w3) / 2;
  ctx.textAlign = "left";
  ctx.fillStyle = OFF; ctx.fillText(t1, tx, 390); tx += w1;
  ctx.fillStyle = ORANGE; ctx.fillText(t2, tx, 390); tx += w2;
  ctx.fillStyle = OFF; ctx.fillText(t3, tx, 390);

  circle(ctx, meImg, match.me.first_name, W / 2 - 230, 640, 170);
  circle(ctx, partnerImg, match.partner.first_name, W / 2 + 230, 640, 170);
  ctx.textAlign = "center";
  ctx.font = "110px Anton";
  ctx.fillStyle = ORANGE;
  ctx.fillText("X", W / 2, 680);

  ctx.font = "96px Anton";
  ctx.fillStyle = OFF;
  ctx.fillText(`${match.me.first_name.toUpperCase()} + ${match.partner.first_name.toUpperCase()}`, W / 2, 960);
  ctx.font = "600 44px Inter";
  ctx.fillStyle = "rgba(245,245,242,0.85)";
  ctx.fillText(`${match.race_name.toUpperCase()} · ${(categoryLabel || match.partner.category_label || "").toUpperCase()}`, W / 2, 1035);
  ctx.font = "500 36px Inter";
  ctx.fillStyle = ORANGE;
  ctx.fillText(`${match.score}% ${t("img_compat")}`, W / 2, 1100);

  ctx.font = "56px Anton";
  ctx.fillStyle = OFF;
  ctx.fillText(t("img_tag"), W / 2, 1250);
  ctx.fillStyle = ORANGE;
  ctx.fillRect(0, H - 14, W, 14);
  return new Promise((resolve) => canvas.toBlob(resolve, "image/png"));
}

export const ShareCard = ({ match }) => {
  const { t } = useT();
  const L = useLabels();
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);

  const build = async () => {
    setBusy(true);
    try {
      const blob = await renderShareCard(match, t, L.category(match.partner));
      const file = new File([blob], `roxpair-${match.partner.first_name.toLowerCase()}.png`, { type: "image/png" });
      setPreview(URL.createObjectURL(blob));
      if (navigator.canShare?.({ files: [file] })) {
        await navigator.share({ files: [file], title: "ROXPAIR", text: `${match.me.first_name.toUpperCase()} + ${match.partner.first_name.toUpperCase()} · ${match.race_name}` }).catch(() => {});
      } else {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = file.name;
        a.click();
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-3" data-testid="share-card">
      {preview && <img src={preview} alt={t("share_alt")} data-testid="share-card-preview" className="w-full rounded-[4px] border border-rox-line animate-rise-in" />}
      <button data-testid="share-card-button" className="btn-ghost" onClick={build} disabled={busy}>
        {navigator.canShare ? <Share2 size={18} /> : <Download size={18} />} {busy ? "..." : preview ? t("share_again") : t("share_match")}
      </button>
    </div>
  );
};
